import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { FormGroup } from '@angular/forms';

import { CommunicationService } from '../../services/communication.service';
import { LocalStorageService } from '../../services/local-storage.service';
import {
  BusinessFormData,
  BusinessPartnerResponse,
  ConnectionObjectFormData,
  ConnectionObjectResponse,
  ConstructorData,
  InstallerCompanyFormData,
  InstallerData,
  InvoiceAddressFormData,
  UpdateBusinessPartnerData,
  UpdateBusinessPartnerResponse,
  UpdateConnectionObjectData,
  UpdateConnectionObjectResponse,
  UpdateInstallerData,
  UpdateInvoiceAddressData,
} from '../jurisdiction.domain';
import { CommonService } from '../../services/common.service';
import { TechnicianData } from '../../domains/common.domain';
import { EnvironmentService } from '../../services/environment.service';
import { AreaLocation } from 'src/app/+grid-application-shared/entities/personal-data/areaLocation';

@Injectable()
export class PersonalDataService {
  triggerOnConnectionAddressFormUpdate$: Subject<FormGroup>;
  triggerOnInstallerFormUpdate$: Subject<FormGroup>;
  triggerOnReuseDataCheckboxSelection$: Subject<FormGroup>;
  triggerOnSubscriberFormUpdate$: Subject<FormGroup>;
  triggerOnInstallerCompanyFormUpdate$: Subject<FormGroup>;
  triggerOnInstallerSystemFormUpdate$: Subject<FormGroup>;
  triggerInvoiceAddressFormUpdate$: Subject<FormGroup>;
  connectionAddress: FormGroup;

  constructor(
    private commonService: CommonService,
    private communicationService: CommunicationService,
    private environmentService: EnvironmentService,
    private localStorageService: LocalStorageService
  ) {
    this.triggerOnConnectionAddressFormUpdate$ = new Subject<FormGroup>();
    this.triggerOnInstallerFormUpdate$ = new Subject<FormGroup>();
    this.triggerOnReuseDataCheckboxSelection$ = new Subject<FormGroup>();
    this.triggerOnSubscriberFormUpdate$ = new Subject<FormGroup>();
    this.triggerOnInstallerCompanyFormUpdate$ = new Subject<FormGroup>();
    this.triggerOnInstallerSystemFormUpdate$ = new Subject<FormGroup>();
    this.triggerInvoiceAddressFormUpdate$ = new Subject<FormGroup>();
    this.connectionAddress = new FormGroup({});
  }

  get registerForConnectionAddressUpdate(): Subject<FormGroup> {
    return this.triggerOnConnectionAddressFormUpdate$;
  }

  get registerForInstallerUpdate(): Subject<FormGroup> {
    return this.triggerOnInstallerFormUpdate$;
  }

  get registerForSubscriberUpdate(): Subject<FormGroup> {
    return this.triggerOnSubscriberFormUpdate$;
  }

  get registerForAddressReuseUpdate(): Subject<FormGroup> {
    return this.triggerOnReuseDataCheckboxSelection$;
  }

  get registerForInstallerCompanyUpdate(): Subject<FormGroup> {
    return this.triggerOnInstallerCompanyFormUpdate$;
  }

  get registerForInstallerSystemUpdate(): Subject<FormGroup> {
    return this.triggerOnInstallerSystemFormUpdate$;
  }

  get registerForInvoiceAddressUpdate(): Subject<FormGroup> {
    return this.triggerInvoiceAddressFormUpdate$;
  }

  reuseSubscriberData(data: FormGroup): void {
    this.triggerOnReuseDataCheckboxSelection$.next(data);
  }

  updatePersonalDataForm(data: FormGroup, formName: string): void {
    if (formName === 'connectionAddress') {
      this.connectionAddress = data;
      this.triggerOnConnectionAddressFormUpdate$.next(data);
    } else if (formName === 'subscriber') {
      this.triggerOnSubscriberFormUpdate$.next(data);
    } else if (formName === 'responsibleInstaller') {
      this.triggerOnInstallerFormUpdate$.next(data);
    } else if (formName === 'installerCompany') {
      this.triggerOnInstallerCompanyFormUpdate$.next(data);
    } else if (formName === 'installerSystem') {
      this.triggerOnInstallerSystemFormUpdate$.next(data);
    } else if (formName === 'invoiceAddress') {
      this.triggerInvoiceAddressFormUpdate$.next(data);
    }
  }

  parseBusinessPartnerData(data: UpdateBusinessPartnerData): BusinessFormData {
    return {
      addressSuffix: data.address_suffix,
      city: data.city,
      email: data.email,
      firstName: data.firstname,
      lastName: data.lastname,
      mobile: data.mobile,
      telephone: data.phone,
      postCode: data.postcode,
      title: data.sk_title,
      company: data.company_name,
      street: data.street,
      streetNo: data.street_no,
      con_user_firstname: data.con_user_firstname,
      con_user_lastname: data.con_user_lastname,
      con_user_phone: data.con_user_phone,
    };
  }

  parseConnectionObjectData(data: UpdateConnectionObjectData): ConnectionObjectFormData {
    let result: ConnectionObjectFormData = {
      addressSuffix: data.address_suffix,
      buildingType: data.sk_development_area_type,
      buildingZone: data.building_area_name,
      city: data.city,
      district: data.district,
      floor: data.floor,
      gps: data.gps,
      noAddressCheckbox: data.no_street_address === '1',
      parcel: data.parcel,
      postCode: data.postcode,
      housingUnits: data.residential_unit_count,
      apartmentNo: data.residential_unit_no,
      cadastralSection: data.section_name,
      street: data.street,
      streetNo: data.street_no,
      swsDevAreaSubDistrict: data.sws_devarea_subdistrict,
      swsDevAreaName: data.sws_devarea_name,
      swsParcel: data.sws_devarea_parcel,
      swsParcelNumber: data.sws_devarea_parcel_no,
      customFields: {},
    };

    result.customFields = JSON.parse(data.serialized_custom_fields || null) || { ...result };

    return result;
  }

  parseInstallerData(): any {
    const technicianData = this.commonService.gridConnectionApplicationData;
    return {
      gas: {
        taxId: technicianData.gasgridcon.resp_technician_ident || '',
        companyName: technicianData.gasgridcon.resp_technician_company || '',
        telephoneNumber: technicianData.gasgridcon.resp_technician_phone || '',
      },
      heating: {
        taxId: technicianData.districtheatgridcon.resp_technician_ident || '',
        companyName: technicianData.districtheatgridcon.resp_technician_company || '',
        telephoneNumber: technicianData.districtheatgridcon.resp_technician_phone || '',
      },
      power: {
        taxId: technicianData.powergridcon.resp_technician_ident || '',
        companyName: technicianData.powergridcon.resp_technician_company || '',
        telephoneNumber: technicianData.powergridcon.resp_technician_phone || '',
        bdewId: technicianData.powergridcon.technician_bdew_id,
      },
      water: {
        taxId: technicianData.watergridcon.resp_technician_ident || '',
        companyName: technicianData.watergridcon.resp_technician_company || '',
        telephoneNumber: technicianData.watergridcon.resp_technician_phone || '',
      },
    };
  }

  parseInstallerCompanyData(constructorData: ConstructorData, installerData: InstallerData): InstallerCompanyFormData {
    const technicianData: TechnicianData = this.commonService.technicianData || null;
    return {
      company: installerData?.company_name,
      company_city: installerData?.city,
      company_firstName: installerData?.firstname,
      company_name: installerData?.lastname,
      company_postCode: installerData?.postcode,
      company_street: installerData?.street,
      company_streetNumber: installerData?.street_no,
      company_taxId: installerData?.identification,
      company_telephone: installerData?.phone,
      company_telephoneCentral: installerData?.company_phone,
      company_title: installerData?.sk_title,
      system_city: constructorData?.city || (technicianData?.company && technicianData.company[0] && technicianData.company[0].city),
      system_company:
        constructorData?.company_name || (technicianData?.company && technicianData.company[0] && technicianData.company[0].company_name),
      system_firstName: constructorData?.firstname || (technicianData && technicianData.first_name),
      system_name: constructorData?.lastname || (technicianData && technicianData.last_name),
      system_postCode:
        constructorData?.postcode || (technicianData?.company && technicianData.company[0] && technicianData.company[0].postcode),
      system_street: constructorData?.street || (technicianData?.company && technicianData.company[0] && technicianData.company[0].street),
      system_streetNumber:
        constructorData?.street_no || (technicianData?.company && technicianData.company[0] && technicianData.company[0].street_number),
      system_taxId:
        constructorData?.identification || (technicianData?.company && technicianData.company[0] && technicianData.company[0].tax_id),
      system_telephone: constructorData?.phone || (technicianData && technicianData.telephone),
      system_telephoneCentral:
        constructorData?.company_phone || (technicianData?.company && technicianData.company[0] && technicianData.company[0].telephone),
      system_title: constructorData?.sk_title || (technicianData && technicianData.sk_title),
    };
  }

  parseInvoiceAddressData(data: UpdateInvoiceAddressData): InvoiceAddressFormData {
    return {
      id: data?.recipient_id || '',
      land: data?.country || '',
      mobile: data?.mobile || '',
      telephone: data?.phone || '',
      postCode: data?.postcode || '',
      addressSuffix: data?.address_suffix || '',
      streetNo: data?.street_no || '',
      street: data?.street || '',
      lastName: data?.lastname || '',
      firstName: data?.firstname || '',
      title: data?.sk_title || '',
      city: data?.city || '',
      email: data?.email || '',
      company: data?.company_name,
    };
  }

  prepareConnectionRequestData(
    data: ConnectionObjectFormData,
    connectionObjectId: string,
    areaLocations?: AreaLocation[]
  ): UpdateConnectionObjectData {
    if (!this.environmentService.shouldSerializeCustomFields) {
      data = Object.assign({}, data.customFields, data);
    }

    const requestObject: UpdateConnectionObjectData = {
      address_suffix: data.addressSuffix || '',
      building_area_name: data.buildingZone || '',
      city: data.city || '',
      district: data.district || '',
      floor: data.floor || '',
      gps: data.gps || '',
      no_street_address: data.noAddressCheckbox ? '1' : '0',
      parcel: data.parcel || '',
      postcode: data.postCode || '',
      residential_unit_count: data.housingUnits || '',
      residential_unit_no: data.apartmentNo || '',
      section_name: data.cadastralSection || '',
      street: data.street || '',
      street_no: data.streetNo || '',
      area_locations: areaLocations,
    };

    if (this.environmentService.shouldSerializeCustomFields) {
      requestObject.serialized_custom_fields = JSON.stringify(data.customFields);
    }

    if (this.environmentService.showNewDevAreaDetails) {
      requestObject.sws_devarea_name = data.swsDevAreaName || '';
      requestObject.sws_devarea_parcel = data.swsParcel || '';
      requestObject.sws_devarea_parcel_no = data.swsParcelNumber || '';
      requestObject.sws_devarea_subdistrict = data.swsDevAreaSubDistrict || '';
    }
    if (data.buildingType) {
      requestObject.sk_development_area_type = data.buildingType || '';
    }

    if (!connectionObjectId) {
      requestObject.application_id = this.localStorageService.getItem('application_id');
    }

    return requestObject;
  }

  prepareBusinessRequestData(data: BusinessFormData, businessPartnerId: string): UpdateBusinessPartnerData {
    const showConnectionUser = this.environmentService.showConnectionUser;

    const requestObject: UpdateBusinessPartnerData = {
      address_suffix: data.addressSuffix || '',
      city: data.city || '',
      email: data.email || '',
      firstname: data.firstName || '',
      lastname: data.lastName || '',
      mobile: data.mobile || '',
      phone: data.telephone || '',
      postcode: data.postCode || '',
      sk_title: data.title || null,
      company_name: data.company || '',
      street: data.street || '',
      street_no: data.streetNo || '',
    };
    if (showConnectionUser) {
      requestObject.con_user_firstname = data.con_user_firstname;
      requestObject.con_user_lastname = data.con_user_lastname;
      requestObject.con_user_phone = data.con_user_phone;
    }

    if (!businessPartnerId) {
      requestObject.application_id = this.localStorageService.getItem('application_id');
    }

    return requestObject;
  }

  prepareConstructorRequestObject(data: InstallerCompanyFormData): ConstructorData {
    return {
      installation_id: this.localStorageService.getItem('installation_id'),
      postcode: data.system_postCode,
      city: data.system_city,
      street: data.system_street,
      street_no: data.system_streetNumber,
      identification: data.system_taxId,
      company_name: data.system_company,
      firstname: data.system_firstName,
      lastname: data.system_name,
      phone: data.system_telephone,
      company_phone: data.system_telephoneCentral,
      sk_title: data.system_title || null,
    };
  }

  /*  prepareInstallerRequestObject(data: InstallerCompanyFormData): InstallerData {
   return {
   installation_id: this.localStorageService.getItem('installation_id'),
   postcode: data.company_postCode,
   city: data.company_city,
   street: data.company_street,
   street_no: data.company_streetNumber,
   identification: data.company_taxId,
   company_name: data.company,
   firstname: data.company_firstName,
   lastname: data.company_name,
   phone: data.company_telephone,
   company_phone: data.company_telephoneCentral,
   sk_title: data.company_title || null,
   };
   }*/

  prepareInstallerData(data: any): UpdateInstallerData {
    const heatingData = data?.heating || null;
    const gasData = data?.gas || null;
    const powerData = data?.power || null;
    const waterData = data?.water || null;
    return {
      districtheatgridcon: heatingData
        ? {
            resp_technician_ident: heatingData.taxId,
            resp_technician_company: heatingData.companyName,
            resp_technician_phone: heatingData.telephoneNumber,
          }
        : null,
      gasgridcon: gasData
        ? {
            resp_technician_ident: gasData.taxId,
            resp_technician_company: gasData.companyName,
            resp_technician_phone: gasData.telephoneNumber,
          }
        : null,
      powergridcon: powerData
        ? {
            resp_technician_ident: powerData.taxId,
            resp_technician_company: powerData.companyName,
            resp_technician_phone: powerData.telephoneNumber,
          }
        : null,
      watergridcon: waterData
        ? {
            resp_technician_ident: waterData.taxId,
            resp_technician_company: waterData.companyName,
            resp_technician_phone: waterData.telephoneNumber,
          }
        : null,
    };
  }

  prepareInvoiceAddressData(data: InvoiceAddressFormData): UpdateInvoiceAddressData {
    const requestObject: UpdateInvoiceAddressData = {
      address_suffix: data.addressSuffix || '',
      city: data.city || '',
      country: data.land || '',
      firstname: data.firstName || '',
      lastname: data.lastName || '',
      mobile: data.mobile || '',
      phone: data.telephone || '',
      postcode: data.postCode || '',
      sk_title: data.title || null,
      company_name: data.company || '',
      street: data.street || '',
      street_no: data.streetNo || '',
      email: data.email || '',
    };

    if (!data.id) {
      requestObject.application_id = this.localStorageService.getItem('application_id');
    }

    return requestObject;
  }

  saveConnectionObjectData(urlSegment: string, requestData: any): Observable<ConnectionObjectResponse> {
    return this.communicationService.postData(urlSegment, requestData);
  }

  saveBusinessPartnerData(urlSegment: string, requestData: UpdateBusinessPartnerData): Observable<BusinessPartnerResponse> {
    return this.communicationService.postData(urlSegment, requestData);
  }

  saveInstallerData(urlSegment: string, requestData: any): Observable<any> {
    return this.communicationService.updateData(urlSegment, requestData);
  }

  updateConnectionObjectData(urlSegment: string, requestData: UpdateConnectionObjectData): Observable<UpdateConnectionObjectResponse> {
    return this.communicationService.updateData(urlSegment, requestData);
  }

  updateBusinessPartnerData(urlSegment: string, requestData: UpdateBusinessPartnerData): Observable<UpdateBusinessPartnerResponse> {
    return this.communicationService.updateData(urlSegment, requestData);
  }

  getFormValues(formData: FormGroup): any {
    const data = {};
    if (formData !== undefined && formData !== null && Object.keys(formData).length !== 0) {
      Object.keys(formData.controls).forEach(key => {
        data[key] = formData.controls[key].value;
      });
    }
    return data;
  }
}
